// src/components/PaywallModal.jsx
// Blocking modal shown when trial has expired and no active subscription

import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

/**
 * PaywallModal - Blocking modal when access_granted is false
 *
 * This modal cannot be dismissed - user must subscribe to continue
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 */
export default function PaywallModal({ open }) {
  const navigate = useNavigate();

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <DialogContent>
        <Box sx={{ textAlign: "center", py: 2 }}>
          {/* Lock icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <LockRoundedIcon sx={{ fontSize: 40, color: "grey.600" }} />
          </Box>

          <Typography variant="h5" fontWeight={800} gutterBottom>
            Your Free Trial Has Ended
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 360, mx: "auto" }}
          >
            To continue using AgentBlayna and access all your data, please
            choose a plan that fits your needs.
          </Typography>

          {/* Features reminder */}
          <Box
            sx={{
              bgcolor: "grey.50",
              borderRadius: 2,
              p: 3,
              mb: 4,
              textAlign: "left",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              With a subscription you get:
            </Typography>
            <Stack spacing={1}>
              {[
                "Unlimited client management",
                "AI-powered insights & follow-ups",
                "Commission tracking & forecasting",
                "Priority support",
              ].map((feature) => (
                <Stack
                  key={feature}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <BoltRoundedIcon
                    sx={{ fontSize: 16, color: "primary.main" }}
                  />
                  <Typography variant="body2">{feature}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate("/pricing")}
            sx={{
              py: 1.5,
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            View Plans & Subscribe
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2 }}
          >
            Questions? Contact us at support@agentblayna.com
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
